/**
 * Client-side permission matrix mirroring the backend `requireRole(...)`
 * guards in backend/src/routes/erp/*.ts. The server is always the source
 * of truth; this map only decides which actions/nav the UI advertises so
 * roles are not shown buttons that would 403.
 */
import { useAuth } from '@/contexts/AuthContext';

export type Role =
  | 'Owner'
  | 'Admin'
  | 'Finance'
  | 'Sales'
  | 'Engineer'
  | 'Designer'
  | 'Viewer';

export const PERMISSIONS = {
  // invoices.ts
  'invoices.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'invoices.write': ['Owner', 'Admin', 'Finance', 'Sales'],
  'invoices.delete': ['Owner', 'Admin', 'Finance'],
  'invoices.pay': ['Owner', 'Admin', 'Finance'],
  'invoices.void': ['Owner', 'Admin', 'Finance'],
  /* creditNotes.ts. Deliberately narrower than the invoice's: Sales may raise
     and send an invoice, and giving money back is not the same decision. */
  'credit_notes.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'credit_notes.write': ['Owner', 'Admin', 'Finance'],
  'credit_notes.delete': ['Owner', 'Admin', 'Finance'],
  'credit_notes.void': ['Owner', 'Admin', 'Finance'],
  // bills.ts
  'bills.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'bills.write': ['Owner', 'Admin', 'Finance'],
  // expenses.ts
  'expenses.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'expenses.write': ['Owner', 'Admin', 'Finance', 'Sales', 'Engineer', 'Designer'],
  'expenses.approve': ['Owner', 'Admin', 'Finance'],
  // contracts.ts
  'contracts.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'contracts.write': ['Owner', 'Admin', 'Sales'],
  'contracts.terminate': ['Owner', 'Admin'],
  // quotations.ts
  'quotations.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'quotations.write': ['Owner', 'Admin', 'Sales'],
  'quotations.convert': ['Owner', 'Admin', 'Finance', 'Sales'],
  // customers.ts
  'customers.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'customers.write': ['Owner', 'Admin', 'Finance', 'Sales'],
  'customers.manage': ['Owner', 'Admin', 'Finance'],
  // vendors.ts
  'vendors.read': ['Owner', 'Admin', 'Finance'],
  'vendors.write': ['Owner', 'Admin', 'Finance'],
  // employees.ts
  'employees.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'employees.write': ['Owner', 'Admin'],
  /* leave.ts, mirrors the READ/WRITE split on the router. Finance and Viewer
     can read balances because they can already read salaries; approving an
     absence is a management act.

     Note there is no capability for /my-leave: it is not gated on a role at
     all, because it can only ever show the person asking. */
  'leave.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'leave.write': ['Owner', 'Admin'],
  /*
    bankAccounts.ts. NOT the same list as the route, on purpose.

    GET /bank-accounts is two things at once: the balances page, and the picker
    an expense form uses to name what an expense was paid from. Filing an expense
    is open to Sales, Engineer and Designer, so the route answers them, with
    `balance` and `iban` stripped out of the reply.

    This capability is about the PAGE, which exists to show balances, so it is
    the narrower list. Viewer is off it: a Viewer can read expenses and would
    otherwise reach a screen holding every balance in the company.
  */
  'bank.read': ['Owner', 'Admin', 'Finance'],
  // payroll.ts
  'payroll.read': ['Owner', 'Admin', 'Finance'],
  'payroll.write': ['Owner', 'Admin', 'Finance'],
  // subscriptions.ts
  'subscriptions.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'subscriptions.write': ['Owner', 'Admin', 'Finance'],
  // payments.ts
  'payments.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  /*
    ONE capability for correcting a receipt, not two.

    The ticket asked for `payments.delete` and `payments.reverse`. Nothing in
    this product deletes a payment, so the first names an act that cannot
    happen; and reversing a receipt, refunding it and voiding the document it
    settled are three shapes of the same decision, correcting money, held by
    the same three roles. Two capabilities always granted together are one
    capability with extra steps, and the second one is the one that eventually
    gets forgotten at a call site.

    Same list as `invoices.void` and `expenses.approve`: this is the line this
    product already draws around money, and drawing a different one here would
    be a rule nobody could state out loud.
  */
  'payments.correct': ['Owner', 'Admin', 'Finance'],
  // items.ts — the catalogue is NOT gated on the inventory module, so Sales can
  // write it: somebody adding "the new 500g bag" while on the phone is the point.
  'items.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'items.write': ['Owner', 'Admin', 'Finance', 'Sales'],
  'items.delete': ['Owner', 'Admin', 'Finance'],
  // inventory.ts — stock is the ledger side. Sales may not post an adjustment,
  // because that is a journal entry, the same line expenses.approve draws.
  'stock.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'stock.write': ['Owner', 'Admin', 'Finance'],
  'stock.post': ['Owner', 'Admin', 'Finance'],
  // reports.ts
  'reports.read': ['Owner', 'Admin', 'Finance', 'Viewer'],
  'reports.pdf': ['Owner', 'Admin', 'Finance'],
  // settings.ts
  /* Reading the log is deliberately narrower than reading the pages it
     describes: a diff of an employee update shows a salary. `activity.undo`
     being broad is not a widening — it only says the page may offer A button,
     and the per-row plan from the server then decides. */
  /* Reading a file is reading the record it hangs off, so this matches the
     record pages rather than the tighter activity list: a salesperson who can
     open a quotation can open the quotation's attachment. */
  'files.read': ['Owner', 'Admin', 'Finance', 'Sales', 'Viewer'],
  'files.write': ['Owner', 'Admin', 'Finance', 'Sales', 'Engineer', 'Designer'],
  'activity.read': ['Owner', 'Admin', 'Finance'],
  /*
    drafts.ts, a staged change before anything happens.

    Deliberately the SAME list as `activity.read` rather than the list of the
    page a draft is about. A draft carries an amount, a customer, a bank
    account and sometimes a salary, and it is exactly the activity log read a
    day early. A salesperson may open a quotation; "cut Fatima Al Binali's
    salary to 300" is not a quotation, and it is one row down the same list.

    Deciding one is `payments.correct` again (Owner, Admin, Finance) because
    applying a draft posts to the ledger, and this product has one line for
    that. There is no Accountant role in this product and there never was.
  */
  'drafts.read': ['Owner', 'Admin', 'Finance'],
  'drafts.decide': ['Owner', 'Admin', 'Finance'],
  'activity.undo': ['Owner', 'Admin', 'Finance', 'Sales', 'Engineer', 'Designer'],
  'settings.write': ['Owner', 'Admin'],
  // users.ts
  'users.manage': ['Owner'],
} as const satisfies Record<string, readonly Role[]>;

export type Capability = keyof typeof PERMISSIONS;

export function can(role: string | null | undefined, capability: Capability): boolean {
  if (!role) return false;
  return (PERMISSIONS[capability] as readonly string[]).includes(role);
}

export function useCan(): (capability: Capability) => boolean {
  const { user } = useAuth();
  return (capability) => can(user?.role, capability);
}
